import React, { useEffect, useMemo, useState } from 'react'
import { Avatar, Box, Dialog, IconButton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { type GridColDef } from '@mui/x-data-grid-pro'
import { useModal } from '../../../../../store/ModalProvider'
import CustomButton from '../../CustomButton/CustomButton'
import { AnimatedDataGridPro } from '../../CustomDataGrid'
import { dataGridDensityTransitionSx } from '../../CustomDataGrid/dataGridDensityTransitionSx'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { popupActionButtonSx } from '../popupButtonStyles'
import type { DriversWithoutRestModalData, DriversWithoutRestRow } from '../../../../../types/modal'

type DensityMode = 'compact' | 'standard'

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const dayColumnWidth = 50
const defaultTotalDrivers = 17
const defaultTotalRows = 13
const defaultRowsPerPage = 10

const createEmptyDays = () => Array.from({ length: 14 }, () => '')
const driverAvatarSrc = 'http://localhost:3845/assets/2e5f78f44b20ef67ab74659b2552d4e86c81968d.png'

const defaultRows: DriversWithoutRestRow[] = Array.from({ length: 5 }, (_unused, index) => ({
	id: `driver-rest-${index + 1}`,
	motorista: 'Alexandre Gomes',
	nr: '0137',
	days: createEmptyDays(),
	avatarUrl: driverAvatarSrc,
}))

const CustomModalDriversWithoutRest: React.FC = () => {
	const { isOpen, closeModal, modalData } = useModal()
	const data = modalData as DriversWithoutRestModalData | undefined
	const thresholdDays = data?.thresholdDays ?? 6
	const rows = useMemo(() => {
		if (data?.rows?.length) {
			return data.rows
		}
		return defaultRows
	}, [data])
	const rowsPerPage = data?.rowsPerPage ?? defaultRowsPerPage
	const fallbackTotalDrivers = data?.rows?.length ? rows.length : defaultTotalDrivers
	const fallbackTotalRows = data?.rows?.length ? rows.length : defaultTotalRows
	const totalDrivers = data?.totalDrivers ?? fallbackTotalDrivers
	const totalRows = data?.totalRows ?? fallbackTotalRows
	const [density, setDensity] = useState<DensityMode>('compact')
	const [page, setPage] = useState(0)

	useEffect(() => {
		setPage(0)
	}, [rows, rowsPerPage])

	const pageCount = Math.max(1, Math.ceil(rows.length / rowsPerPage))
	const safePage = Math.min(page, pageCount - 1)
	const startIndex = safePage * rowsPerPage
	const pageRows = rows.slice(startIndex, startIndex + rowsPerPage)

	const displayRows = useMemo(
		() =>
			pageRows.map(row => ({
				...row,
				days: row.days ?? createEmptyDays(),
			})),
		[pageRows],
	)

	const rangeEnd = Math.min(startIndex + displayRows.length, totalRows)
	const pageLabel = totalRows > 0 ? `${startIndex + 1}-${rangeEnd} de ${totalRows}` : '0-0 de 0'

	const columns = useMemo<GridColDef<DriversWithoutRestRow>[]>(() => {
		const dayColumns: GridColDef<DriversWithoutRestRow>[] = Array.from(
			{ length: 14 },
			(_unused, index): GridColDef<DriversWithoutRestRow> => ({
				field: `day${index + 1}`,
				headerName: `dia ${index + 1}`,
				width: dayColumnWidth,
				minWidth: dayColumnWidth,
				headerAlign: 'left' as const,
				align: 'left' as const,
				cellClassName: 'day-cell',
				headerClassName: 'day-header',
				valueGetter: (_value, row) => row.days?.[index] ?? '',
			}),
		)

		return [
			{
				field: 'motorista',
				headerName: 'Motorista',
				width: 200,
				minWidth: 200,
				headerClassName: 'driver-header',
				cellClassName: 'driver-cell',
				renderCell: params => {
					const name = String(params.row.motorista ?? '')
					const initial = name.trim().charAt(0) || 'M'

					return (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<Avatar
								src={params.row.avatarUrl}
								sx={{
									borderRadius: '100%',
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: 'center',
									bgcolor: 'lightgrey 50% / cover no-repeat',
									width: 32,
									height: 32,
									fontSize: '20px',
									fontWeight: 400,
									lineHeight: 1,
								}}
							>
								{initial}
							</Avatar>
							<Typography
								sx={{
									fontFamily: 'Poppins, sans-serif',
									fontSize: '12px',
									lineHeight: 1.3,
									color: '#092056',
								}}
							>
								{name}
							</Typography>
						</Box>
					)
				},
			},
			{
				field: 'nr',
				headerName: 'Nr',
				width: 57,
				minWidth: 57,
				headerClassName: 'nr-header',
				cellClassName: 'nr-cell',
			},
			...dayColumns,
		]
	}, [])

	const rowHeight = density === 'compact' ? 32 : 40
	const headerHeight = 40

	const handleDensityChange = (_event: React.MouseEvent<HTMLElement>, nextDensity: DensityMode | null) => {
		if (nextDensity) {
			setDensity(nextDensity)
		}
	}

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1200px, 96vw)',
					borderRadius: '4px',
					backgroundColor: '#FFFFFF',
					boxShadow: dialogShadow,
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<Box sx={{ fontFamily: 'Poppins, sans-serif', color: '#092056' }}>
				<Box sx={{ padding: '16px 24px' }}>
					<Typography
						sx={{
							fontSize: '16px',
							fontWeight: 600,
							lineHeight: 1.75,
							letterSpacing: '-0.85px',
							color: '#092056',
						}}
					>
						Motoristas Sem Folgas h� mais de {thresholdDays} dias
					</Typography>
				</Box>

				<Box
					sx={{
						padding: '24px 24px 0',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						gap: '16px',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							width: '100%',
						}}
					>
						<Typography
							sx={{
								fontSize: '24px',
								fontWeight: 600,
								lineHeight: 1.334,
								color: '#092056',
							}}
						>
							{totalDrivers} Motoristas sem Folgas
						</Typography>
						<ToggleButtonGroup
							value={density}
							exclusive
							onChange={handleDensityChange}
							size='small'
							sx={{
								border: '1px solid rgba(134, 149, 175, 0.15)',
								borderRadius: '4px',
								'& .MuiToggleButton-root': {
									border: 'none',
									borderRadius: 0,
									padding: '4.8px',
									'&:not(:last-child)': {
										borderRight: '0.6px solid rgba(134, 149, 175, 0.15)',
									},
									'&.Mui-selected': {
										backgroundColor: '#F5F7FC',
										'&:hover': {
											backgroundColor: '#F5F7FC',
										},
									},
								},
							}}
						>
							<ToggleButton
								value='compact'
								aria-label='densidade compacta'
							>
								<TableRowsIcon
									sx={{
										width: '14.4px',
										height: '14.4px',
										color: density === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
									}}
								/>
							</ToggleButton>
							<ToggleButton
								value='standard'
								aria-label='densidade espa\u00e7ada'
							>
								<ViewHeadlineIcon
									sx={{
										width: '14.4px',
										height: '14.4px',
										color: density === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
									}}
								/>
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>

					<Box
						sx={{
							borderRadius: '4px',
							backgroundColor: '#FFFFFF',
							width: '100%',
						}}
					>
						<AnimatedDataGridPro
							slots={{ columnMenu: CustomColumnMenu }}
							slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
							rows={displayRows}
							columns={columns}
							autoHeight
							density={density}
							rowHeight={rowHeight}
							columnHeaderHeight={headerHeight}
							disableRowSelectionOnClick
							hideFooter
							sx={[
								{
									border: 'none',
									'--DataGrid-rowBorderColor': 'transparent',
									'& .MuiDataGrid-columnHeaders': {
										backgroundColor: '#FFFFFF',
										borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
										borderTop: 'none',
										boxShadow: 'none',
									},
									'& .MuiDataGrid-columnHeaderTitle': {
										fontFamily: 'Poppins, sans-serif',
										fontWeight: 600,
										fontSize: '12px',
										color: '#8695AF',
										lineHeight: '24px',
										letterSpacing: '0.17px',
										textTransform: 'none',
										textOverflow: 'clip',
										overflow: 'visible',
									},
									'& .MuiDataGrid-columnHeaderTitleContainer': {
										padding: 0,
									},
									'& .MuiDataGrid-columnHeaderTitleContainerContent': {
										padding: 0,
									},
									'& .MuiDataGrid-columnSeparator': {
										opacity: 0,
										color: 'rgba(134, 149, 175, 0.6)',
										transition: 'opacity 0.2s ease',
									},
									'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
										opacity: 1,
									},
									'& .MuiDataGrid-columnSeparator--resizable': {
										cursor: 'col-resize',
									},
									'& .MuiDataGrid-columnHeader': {
										borderRight: 'none',
										padding: 0,
									},
									'& .MuiDataGrid-cell': {
										fontFamily: 'Poppins, sans-serif',
										fontSize: '12px',
										lineHeight: 1.3,
										color: '#092056',
										borderBottom: 'none',
										borderRight: 'none',
										padding: 0,
									},
									'& .MuiDataGrid-row': {
										borderBottom: 'none',
									},
									'& .MuiDataGrid-row:hover': {
										backgroundColor: 'transparent',
									},
									'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
										outline: 'none',
									},
									'& .MuiDataGrid-cell.driver-cell': {
										padding: '0 4px',
									},
									'& .MuiDataGrid-columnHeader.driver-header': {
										paddingLeft: '4px',
										paddingRight: 0,
									},
									'& .MuiDataGrid-cell.day-cell': {
										padding: '0 16px',
									},
									'& .MuiDataGrid-columnHeader.day-header': {
										paddingLeft: '16px',
										paddingRight: 0,
									},
									'& .MuiDataGrid-cell.nr-cell': {
										padding: '0 16px',
									},
									'& .MuiDataGrid-columnHeader.nr-header': {
										paddingLeft: '16px',
										paddingRight: 0,
									},
								},
								dataGridDensityTransitionSx,
							]}
						/>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'flex-end',
								gap: '26px',
								padding: '2px 0',
							}}
						>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Typography
									sx={{
										fontSize: '11px',
										fontWeight: 400,
										color: '#8695AF',
										lineHeight: 1.3,
									}}
								>
									Linhas por p�gina
								</Typography>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
									<Typography
										sx={{
											fontSize: '11px',
											fontWeight: 400,
											color: '#092056',
											lineHeight: 1.3,
										}}
									>
										{rowsPerPage}
									</Typography>
									<ArrowDropDownIcon sx={{ fontSize: 24, color: '#00A5FF' }} />
								</Box>
							</Box>
							<Typography
								sx={{
									fontSize: '11px',
									fontWeight: 400,
									color: '#092056',
									lineHeight: 1.3,
								}}
							>
								{pageLabel}
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center' }}>
								<IconButton
									size='small'
									sx={{ padding: '8px' }}
									onClick={() => setPage(prev => Math.max(prev - 1, 0))}
								>
									<ChevronLeftRoundedIcon sx={{ fontSize: 24, color: '#00A5FF' }} />
								</IconButton>
								<IconButton
									size='small'
									sx={{ padding: '8px' }}
									onClick={() => setPage(prev => Math.min(prev + 1, pageCount - 1))}
								>
									<ChevronRightRoundedIcon sx={{ fontSize: 24, color: '#00A5FF' }} />
								</IconButton>
							</Box>
						</Box>
					</Box>
				</Box>

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						gap: '8px',
						padding: '8px',
					}}
				>
					<CustomButton
						variant='contained'
						onClick={closeModal}
						sx={popupActionButtonSx}
					>
						Fechar
					</CustomButton>
				</Box>
			</Box>
		</Dialog>
	)
}

export default CustomModalDriversWithoutRest
